import { Box, Container, Flex, Text, TextProps } from '@chakra-ui/react';
import {
  AppLink,
  ChevronDownIcon,
  customScrollbarStyles,
  pxToRem,
  TRANSITIONS,
} from '@cyberkongz/ui';
import { Global } from '@emotion/react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

export function getHeadingId(heading: string | any[]) {
  if (typeof heading === 'string') {
    return heading.replace(/[.:]/, '').replace(/ /g, '-').toLowerCase();
  }
}

function TOCSidebarHeading({
  heading,
  isVisible,
  color,
  fontWeight,
  py,
}: { heading: string; isVisible?: boolean } & TextProps) {
  return (
    <Text
      css={{
        fontWeight: fontWeight || ['normal', null, 'medium'],
        color: color || ['textSecondary', null, isVisible ? 'primary' : 'textSecondary'],
        py: py || [2, 4],
        borderBottom: ['none', null, 'thin'],
        textTransform: 'uppercase',
      }}
    >
      {heading}
    </Text>
  );
}

function TOCSidebarHeadings({
  visibleSection,
  headings,
  onClick,
}: {
  visibleSection: number;
  headings: string[];
  onClick?: () => void;
}) {
  return (
    <>
      {headings.map((heading, i) => {
        const isVisible = visibleSection === i;

        return (
          <AppLink
            key={heading}
            onClick={onClick}
            href={`#${getHeadingId(heading)}`}
            variant={isVisible ? 'noHover' : undefined}
            width="100%"
            display="block"
          >
            <TOCSidebarHeading {...{ heading, isVisible }} />
          </AppLink>
        );
      })}
    </>
  );
}

function TOCSidebar({ visibleSection, headings }: { visibleSection: number; headings: string[] }) {
  const [sidebarMobileOpened, setSidebarMobileOpened] = useState(false);

  return (
    <Box
      css={{
        width: ['100%', null, pxToRem(290)],
        mr: [0, null, 16],
        position: 'sticky',
        top: [pxToRem(82), null, 'var(-scroll-padding-top)'],
        alignSelf: 'flex-start',
        zIndex: 2,
        bg: 'background',
        borderTop: ['thin', null, 'none'],
        borderBottom: ['thin', null, 'none'],
        mb: [6, null, 0],
        overflowY: 'auto',
        maxH: ['65vh', null, '85vh'],
        pr: [4, null, 0],
        ...customScrollbarStyles,
      }}
    >
      <Box display={['block', null, 'none']}>
        <Flex
          css={{ cursor: 'pointer', alignItems: 'center', justifyContent: 'space-between' }}
          onClick={() => setSidebarMobileOpened(!sidebarMobileOpened)}
        >
          <TOCSidebarHeading
            heading={sidebarMobileOpened ? 'In this document' : headings[visibleSection]}
            isVisible={sidebarMobileOpened ? false : true}
            color={sidebarMobileOpened ? 'text' : undefined}
            fontWeight="medium"
            py={4}
          />
          <Box
            css={{
              '& svg': {
                transition: TRANSITIONS.standard,
                transform: `rotateZ(${sidebarMobileOpened ? '180' : '0'}deg)`,
                w: pxToRem(16),
              },
            }}
          >
            <ChevronDownIcon />
          </Box>
        </Flex>
        {sidebarMobileOpened ? (
          <TOCSidebarHeadings
            visibleSection={visibleSection}
            headings={headings}
            onClick={() => setSidebarMobileOpened(false)}
          />
        ) : null}
      </Box>
      <Box display={['none', null, 'block']}>
        <TOCSidebarHeadings visibleSection={visibleSection} headings={headings} />
      </Box>
    </Box>
  );
}

export function useVisibleSection(headings: string[]) {
  const headingRefs = useRef<HTMLDivElement[]>([]);
  const [visibleSection, setVisibleSection] = useState(0);

  function handleScroll() {
    const { scrollY: scrollTop, innerHeight: height } = window;
    const threshold = height * 0.4;
    let closestHeading = 0;

    headingRefs.current.forEach((ref, i) => {
      const headingOffset = ref.offsetTop;
      const headingPositionFromTop = headingOffset - scrollTop;

      if (headingPositionFromTop < threshold) {
        closestHeading = i;
      }
    });

    setVisibleSection(closestHeading);
  }

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    headingRefs.current = headingRefs.current.slice(0, headings.length);
  }, [headings]);

  return { visibleSection, headingRefs };
}

export function ContentWithTOCWrapper({
  visibleSection,
  headings,
  children,
}: { visibleSection: number; headings: string[] } & PropsWithChildren) {
  return (
    <Container
      css={{
        mb: 32,
        mt: [0, 4, 16],
        display: 'flex',
        color: 'textSecondary',
        flexDirection: ['column', null, 'row'],
        alignItems: 'center',
        mx: 'auto',
      }}
    >
      <Global
        styles={`
          html {
            scroll-behavior: smooth!important;
          }
        `}
      />
      <TOCSidebar {...{ visibleSection, headings }} />
      <Box
        css={{
          flex: 1,
          w: '100%',
          maxWidth: ['100%', null, pxToRem(720)],
        }}
      >
        {children}
      </Box>
    </Container>
  );
}
